/**
 * UpdateFieldsPanel Component
 * Panel za odabir polja koja se ažuriraju pri re-importu
 */

import {
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { UPDATE_FIELDS_LIST } from '../utils/fieldConfig';
import { FeedProfile, ImportMode } from '../types';

interface UpdateFieldsPanelProps {
  updateFields: Record<string, boolean>;
  onUpdateFieldChange: (key: string, checked: boolean) => void;
  selectedProfile: FeedProfile | null;
  importMode?: ImportMode;
  onImportModeChange?: (mode: ImportMode) => void;
  skipDuplicateCheck?: boolean;
  onSkipDuplicateCheckChange?: (checked: boolean) => void;
}

export function UpdateFieldsPanel({
  updateFields,
  onUpdateFieldChange,
  selectedProfile,
  importMode = 'create_and_update',
  onImportModeChange,
  skipDuplicateCheck = false,
  onSkipDuplicateCheckChange,
}: UpdateFieldsPanelProps) {
  return (
    <>
      <Divider sx={{ my: 2 }} />

      {/* Import Mode Selector */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Način importa</InputLabel>
          <Select
            value={importMode}
            label="Način importa"
            onChange={(e) => onImportModeChange?.(e.target.value as ImportMode)}
          >
            <MenuItem value="create_and_update">
              Kreiraj i ažuriraj (standardno)
            </MenuItem>
            <MenuItem value="update_only">
              Samo ažuriraj postojeće (po SKU)
            </MenuItem>
            <MenuItem value="create_only">
              Samo kreiraj nove
            </MenuItem>
          </Select>
        </FormControl>
        {importMode === 'update_only' && (
          <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.7rem' }}>
            Proizvodi koji ne postoje u bazi će biti preskočeni!
          </Alert>
        )}
        {importMode === 'create_only' && (
          <Alert severity="info" sx={{ mt: 1, py: 0, fontSize: '0.7rem' }}>
            Postojeći proizvodi neće biti ažurirani!
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Skip Duplicate Check */}
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={skipDuplicateCheck}
              onChange={(e) => onSkipDuplicateCheckChange?.(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Preskoči provjeru duplikata</Typography>}
          sx={{ m: 0 }}
        />
        {skipDuplicateCheck && (
          <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.7rem' }}>
            Proizvodi čiji SKU već postoji kod drugog dobavljača će biti preskočeni (neće se spojiti).
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Polja za ažuriranje
        </Typography>
        {selectedProfile && (
          <Typography variant="caption" color="primary">
            (po profilu)
          </Typography>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: '0.7rem' }}>
        Sprema se uz profil. Označena polja će se ažurirati pri ponovnom importu.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {UPDATE_FIELDS_LIST.map(field => (
          <FormControlLabel
            key={field.key}
            control={
              <Checkbox
                size="small"
                checked={updateFields[field.key] ?? false}
                onChange={(e) => onUpdateFieldChange(field.key, e.target.checked)}
              />
            }
            label={<Typography variant="body2">{field.label}</Typography>}
            sx={{ m: 0, height: 28 }}
          />
        ))}
      </Box>
    </>
  );
}
